/**
 * Tools for rendering collections of Articles.
 *
 * @module
 */
import * as React from 'react';
import { ArticleFragmentContext, ArticlesContext } from '../contexts';
import { useArticles } from '../hooks';
import { CollectionChildren, LoopItemChildren, } from '../componentsInternal/utils';
/**
 * Either prints out or passes (in the case of a render prop as children) the total number of articles in a given category.
 *
 * - If no `categoryId` provided, will use the current category context.
 */
export function TotalCount({ children, categoryId }) {
    const { totalCount } = useArticles({ categoryId });
    if (children)
        return React.createElement(React.Fragment, null, children({ totalCount }));
    return React.createElement(React.Fragment, null, totalCount);
}
TotalCount.displayName = 'Articles.TotalCount';
/**
 * Provides a way to access the categories data via a render prop.
 *
 * Utilises [[useArticles]] under the hood.
 */
export function Collection({ children, categoryId }) {
    const { articles } = useArticles({ categoryId });
    return (React.createElement(CollectionChildren, { data: articles, Provider: ArticlesContext.Provider }, children));
}
Collection.displayName = 'Articles.Collection';
/**
 * Component that repeats children passed in for one of the following:
 * 1. each article of the current category in context.
 * 2. for the `categoryId` passed into the function.
 *
 * - If you pass a render prop as a child it will be called with each sub category result.
 * - Otherwise if you pass in React Elements, they will be repeated for each sub category result.
 *
 * Usage example:
 * ```javascript
 * export const LoopedArticles = (props: {
 *   categoryId?: string;
 *   limit?: number;
 * }) => {
 *   const { categoryId, limit } = props;
 *   return (
 *     <Articles.Loop categoryId={categoryId} limit={limit}>
 *       <div className="mb-6 block">
 *         <Article.Link className="mb-2" data-testid="article-link">
 *           <Article.Title />
 *         </Article.Link>
 *         <Article.Summary className="text-grey-light text-sm" />
 *       </div>
 *     </Articles.Loop>
 *   );
 * };
 * ```
 */
export const Loop = ({ children, limit, categoryId }) => {
    const { articles } = useArticles({ limit, categoryId });
    if (!articles)
        return null;
    return (React.createElement(LoopItemChildren, { data: articles, dataKey: "article", Provider: ArticleFragmentContext.Provider }, children));
};
Loop.displayName = 'Articles.Loop';
/******************
 * Loop end
 ******************/
//# sourceMappingURL=articles.js.map